import './trouble.css'
import { Button, Icons, P } from '@feral/ui'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { checkForUpdate } from '../lib/update.js'

/**
 * What a screen shows when it crashes, in place of the router's bare message. The usual cause is an
 * installed app still running an older build against a newer server, or asking for a piece of the app
 * the server no longer has. That build can't reach the "New version ready" notice (PwaBridge) from a
 * crash, so this screen takes the update itself: Update looks for a new version, lets a waiting one
 * take over and reloads onto it; with nothing newer it reloads anyway. Loaded with the shell, never
 * lazily, so it still works when the rest of the app can't be fetched.
 */
export function ErrorScreen({ error }: ErrorComponentProps) {
  const [waiting, setWaiting] = useState(false)
  const [busy, setBusy] = useState(false)
  const [note, setNote] = useState<string | null>(null)
  useEffect(() => {
    navigator.serviceWorker
      ?.getRegistration()
      .then((reg) => setWaiting(!!reg?.waiting))
      .catch(() => {})
  }, [])
  const update = async () => {
    setBusy(true)
    setNote(null)
    const r = await checkForUpdate().catch(() => 'latest' as const)
    if (r === 'offline') {
      setBusy(false)
      setNote("You're offline. Reconnect and try again.")
    } else if (r === 'latest') window.location.reload()
  }
  const message = error instanceof Error ? error.message : String(error ?? '')
  return (
    <div className="fm-crash" role="alert">
      <span className="fm-crash__icon">{Icons.alert(28)}</span>
      <h1 className="fm-crash__title">Something went wrong</h1>
      <P muted>
        {waiting
          ? 'A new version of the app is ready, and it should fix this.'
          : 'Updating gets the newest version of the app and starts it again.'}
      </P>
      <Button variant="primary" disabled={busy} onClick={update}>
        {busy ? 'Updating…' : 'Update'}
      </Button>
      {note && <P sm>{note}</P>}
      {message && <p className="fm-crash__detail fm-sm fm-muted">{message}</p>}
    </div>
  )
}
